import { Link } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, FileText, Trees, Scale, TrendingUp, Users } from "lucide-react";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import Breadcrumb from "../components/common/Breadcrumb";

const RessourcesPage = () => {
  useEffect(() => {
    document.title = "Ressources sur le Foncier Forestier | Sylva Invest";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Guides pratiques sur le remembrement forestier, le morcellement, les transactions et les successions forestières. Ressources pour propriétaires.');
    }
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', 'https://sylva-invest.com/ressources');
    return () => {
      document.title = "Sylva Invest — Transactions Forestières, Remembrement & Conseil (France)";
      if (metaDescription) metaDescription.setAttribute('content', 'Sylva Invest accompagne propriétaires, héritiers et investisseurs pour recomposer le foncier forestier morcelé.');
      if (canonical) canonical.setAttribute('href', 'https://sylva-invest.com/');
    };
  }, []);

  const guides = [
    {
      title: "Remembrement Forestier",
      description: "Guide complet sur le remembrement forestier : définition, méthodes de regroupement parcellaire et solutions pour les propriétaires.",
      href: "/remembrement-forestier",
      icon: Trees,
      category: "Guide pratique"
    },
    {
      title: "Vendre une Parcelle de Bois",
      description: "Comment vendre une parcelle forestière au meilleur prix ? Estimation, fiscalité, succession, indivision : toutes les étapes.",
      href: "/vendre-parcelle-bois",
      icon: TrendingUp,
      category: "Guide pratique"
    },
    {
      title: "Morcellement Forestier",
      description: "Comprendre le morcellement forestier, ses conséquences économiques et les solutions de regroupement foncier.",
      href: "/morcellement-forestier",
      icon: FileText,
      category: "Guide pratique"
    },
    {
      title: "Transactions Forestières",
      description: "Achat, vente et regroupement de parcelles forestières : nos services d'intermédiation réglementée (Carte T).",
      href: "/transactions-forestieres",
      icon: Scale,
      category: "Services"
    },
    {
      title: "Successions Forestières",
      description: "Succession forestière : vendre ou conserver une forêt héritée ? Guide sur l'indivision, le partage et les options.",
      href: "/successions-forestieres",
      icon: Users,
      category: "Guide pratique"
    }
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Ressources sur le Foncier Forestier",
    "description": "Guides pratiques sur le remembrement forestier, le morcellement, les transactions et les successions forestières.",
    "url": "https://sylva-invest.com/ressources",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": guides.map((guide, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": guide.title,
        "description": guide.description,
        "url": `https://sylva-invest.com${guide.href}`
      }))
    }
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }} />
      <Navigation />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-forest text-white py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Breadcrumb items={[{ label: "Ressources" }]} />
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
              <p className="text-xs uppercase tracking-[0.2em] text-gold font-sans font-semibold mb-4">Centre de Ressources</p>
              <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight mb-6" data-testid="ressources-h1">
                Comprendre le Foncier Forestier
              </h1>
              <p className="font-sans text-base md:text-lg text-white/80 leading-relaxed">
                Guides pratiques pour les propriétaires forestiers : remembrement, morcellement, 
                transactions, successions et regroupement de parcelles en France.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Guides Pratiques */}
        <section className="py-16 md:py-24 bg-cream">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.2em] text-gold font-sans font-semibold mb-2">Guides Pratiques</p>
              <h2 className="font-serif text-2xl md:text-3xl font-normal tracking-tight text-forest mb-4">
                Nos Articles sur le Foncier Forestier
              </h2>
              <p className="font-sans text-gray-600 max-w-2xl mx-auto">
                Des guides complets pour comprendre les enjeux du foncier forestier et prendre les bonnes décisions.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {guides.map((guide, index) => (
                <motion.article
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white p-8 border-l-4 border-gold hover:shadow-lg transition-shadow"
                  data-testid={`guide-card-${index}`}
                >
                  <p className="text-xs uppercase tracking-wider text-gold font-sans font-semibold mb-2">{guide.category}</p>
                  <guide.icon className="w-10 h-10 text-gold mb-4" />
                  <h3 className="font-serif text-xl text-forest mb-3">{guide.title}</h3>
                  <p className="font-sans text-sm text-gray-600 leading-relaxed mb-4">{guide.description}</p>
                  <Link to={guide.href} className="inline-flex items-center text-sm font-semibold text-forest hover:text-gold transition-colors">
                    Lire le guide <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-bone">
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="font-serif text-2xl md:text-3xl font-normal tracking-tight text-forest mb-4">
                Besoin d'un Accompagnement Personnalisé ?
              </h2>
              <p className="font-sans text-gray-600 max-w-2xl mx-auto mb-8">
                Sylva Invest vous accompagne dans vos projets de recomposition foncière, 
                de remembrement et de transactions forestières.
              </p>
              <Link to="/#contact" className="btn-primary inline-block">Nous Contacter</Link>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default RessourcesPage;
